import { Observable, of, Subject } from "rxjs";
import {
  httpClient,
  _getApiAuthCredentials,
  _unwrapAxiosResponse,
  _getCognitoAuthorizeEndpoint,
  _getCognitoTokenEndpoint,
  _getCognitoLogoutEndpoint,
  STORAGE_KEY_VALID_UNTIL,
  STORAGE_KEY_ACCESS_TOKEN,
  STORAGE_KEY_ACCESS_TOKEN_SCOPE,
  STORAGE_KEY_ACCESS_TOKEN_TYPE,
  STORAGE_KEY_REFRESH_TOKEN,
  STORAGE_KEY_ID_TOKEN,
  STORAGE_KEY_NAME,
} from "./http";
import env from "../../environments/env.json";
import { stringify } from "query-string";
import base64url from "base64url";

const _getIncrementUrl = (sequenzName: string) => {
  // TODO: Dies ist nur ein Beispiel-Endpunkt
  return `/${sequenzName}/next-id`;
};
const _getApiScope = () => {
  // TODO: Aus LocalStorage heraussuchen
  return "termine/belegungsplan.read";
};

const isAppClientConfigured = () => {
  return !!_getApiAuthCredentials();
};

const listResourceScopes = (): Observable<any[]> => {
  const resultSubject = new Subject<any[]>();

  _unwrapAxiosResponse(httpClient.get("/admin/app-client"), resultSubject);

  return resultSubject;
};

const createAppClient = (name: string, scope: string[]): Observable<any> => {
  const createSubject = new Subject<any>();

  _unwrapAxiosResponse(
    httpClient.put("/admin/app-client", { name, scope }),
    createSubject
  );

  return createSubject;
};

const createAdminUser = (
  username: string,
  otpSecret: string
): Observable<any> => {
  const createSubject = new Subject<any>();

  _unwrapAxiosResponse(
    httpClient.put("/admin/admin-user", { username, otpSecret }),
    createSubject
  );

  return createSubject;
};

const createUser = (username: string): Observable<any> => {
  const createSubject = new Subject<any>();

  _unwrapAxiosResponse(
    httpClient.put("/admin/user", { username }),
    createSubject
  );

  return createSubject;
};

const needs2FA = (username: string): Observable<boolean> => {
  console.log("Dies ist ein 2FA-Request");
  return of(false);
};

const login = (scope: string, appComponentsData: any) => {
  console.log("Dies ist ein Loginversuch");

  if (!scope) {
    scope = "openid profile";
  }
  const state = [...Array(10)]
    .map((_) => ((Math.random() * 36) | 0).toString(36))
    .join("");
  console.log("Generated state string: " + state);
  localStorage.setItem(
    `state-${state}`,
    JSON.stringify(
      Object.assign(
        { url: window.location.pathname, time: new Date().getTime() },
        appComponentsData
      )
    )
  );

  const params: any = { state, scope };
  params["response_type"] = "code";
  params["client_id"] = env.auth.clientId;
  params["redirect_uri"] = env.auth.redirectUri;
  params["identity_provider"] = "COGNITO";

  const url =
    _getCognitoAuthorizeEndpoint() +
    "?" +
    stringify(params).replace("%20", " ");
  console.log("Starte AUTH Anfrage an URL: " + url);
  window.location.href = url;
};

const logout = (forceLogin: boolean) => {
  console.log("Dies ist ein Logout");

  const state = [...Array(10)]
    .map((_) => ((Math.random() * 36) | 0).toString(36))
    .join("");
  console.log("Generated state string: " + state);
  localStorage.setItem(
    `state-${state}`,
    JSON.stringify({
      url: window.location.pathname,
      time: new Date().getTime(),
    })
  );

  const scope = "openid profile";
  const params: any = { };
  params["identity_provider"] = "COGNITO";
  params["client_id"] = env.auth.clientId;
  if (forceLogin) {
    params["response_type"] = "code";
    params["redirect_uri"] = env.auth.redirectUri;
    params.state = state;
    params.scope = scope;
  } else {
    params["logout_uri"] = env.auth.logoutUri;
  }

  const url =
    _getCognitoLogoutEndpoint() +
    "?" +
    stringify(params).replace("%20", " ");
  console.log("Starte LOGOUT Anfrage an URL: " + url);
  window.location.href = url;
};

const exchangeAuthCode = (code: string): Observable<any> => {
  const postParams: any = { code };
  postParams["grant_type"] = "authorization_code";
  postParams["redirect_uri"] = env.auth.redirectUri;
  postParams["client_id"] = env.auth.clientId;

  const authString = btoa(_getApiAuthCredentials());
  const headers = {
    Authorization: "Basic " + authString,
    Accept: "*/*",
    "Content-Type": "application/x-www-form-urlencoded",
  };

  const authResultSubject = new Subject<any>();
  _unwrapAxiosResponse(
    httpClient.post(
      _getCognitoTokenEndpoint(),
      stringify(postParams).replace("%20", " "),
      {
        headers,
      }
    )
  ).subscribe(
    (authResponse: any) => {
      const encodedAccessTokenPayload = authResponse.access_token.split(".")[1];
      const accessTokenPayload: any = JSON.parse(
        base64url.decode(encodedAccessTokenPayload)
      );
      const idTokenPayload: any = base64url.decode(
        authResponse.id_token.split(".")[1]
      );
      const idToken = JSON.parse(idTokenPayload);
      localStorage.setItem(
        STORAGE_KEY_ACCESS_TOKEN_SCOPE,
        accessTokenPayload.scope
      );
      localStorage.setItem(
        STORAGE_KEY_VALID_UNTIL,
        "" + (authResponse.expires_in * 1000 + Date.now() - 5000)
      );
      localStorage.setItem(
        STORAGE_KEY_ACCESS_TOKEN_TYPE,
        authResponse.token_type
      );
      localStorage.setItem(STORAGE_KEY_ACCESS_TOKEN, authResponse.access_token);
      localStorage.setItem(
        STORAGE_KEY_REFRESH_TOKEN,
        authResponse.refresh_token
      );
      localStorage.setItem(STORAGE_KEY_ID_TOKEN, idTokenPayload);
      localStorage.setItem(
        STORAGE_KEY_NAME,
        `${idToken.given_name || ""} ${idToken.name || ""}`.trim()
      );

      authResultSubject.next(authResponse);
      authResultSubject.complete();
    },
    (reason) => {
      authResultSubject.error(reason);
    }
  );
  return authResultSubject;
};

const cleanupLocalStorage = (): void => {
  localStorage.removeItem(STORAGE_KEY_VALID_UNTIL);
  localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN_SCOPE);
  localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN_TYPE);
  localStorage.removeItem(STORAGE_KEY_REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEY_ID_TOKEN);
  localStorage.removeItem(STORAGE_KEY_NAME);
  Object.keys(localStorage)
    .filter(key => key.startsWith("state-"))
    .map(key => localStorage.removeItem(key));
};

const incrementAndGet = (sequenzName: string): Observable<number> => {
  const newSequenceNumberSubject = new Subject<number>();
  _unwrapAxiosResponse(
    httpClient.put(_getIncrementUrl(sequenzName), { sequenzName }, {
      requiredRoles: "termine/belegungsplan.write",
    } as any),
    //httpClient.put(`/studio/bitte-aendern`, { bezeichner: 'bitte-aendern', name: 'Darmstadt' }, {requiredRoles: 'studios/schreibzugriff'} as any),
    //httpClient.put(`/studio/darmstadt/termin`, { standort: 1, zeit: currentTimestamp(), dauer: 1.5, dame: 1, massageart: 2, gast: 'Peter', geblockt: false, abgesagt: false }, {requiredRoles: 'studios/lesezugriff termine/schreibzugriff studio/darmstadt/termine.schreibzugriff'} as any),
    //httpClient.put(`/studio/darmstadt/termin`, { standort: 1, zeit: currentTimestamp, dauer: 1.5, dame: 1, massageart: 2, gast: 'Peter', geblockt: false, abgesagt: false }, {requiredRoles: 'studios/lesezugriff termine/schreibzugriff studio/darmstadt/termine.schreibzugriff'} as any),
    //httpClient.put(`/dame`, { kuenstlername: 'Anna', name: 'Anna Maria Lustig' }, {requiredRoles: 'damen/schreibzugriff'} as any),
    //httpClient.put(`/studio/darmstadt/termin`, { standort: {id: 1, bezeichner: 'darmstadt', name: 'Darmstadt'}, zeit: currentTimestamp(), dauer: 1.5, dame: 1, massageart: 2, gast: 'Peter', geblockt: false, abgesagt: false }, {requiredRoles: 'studios/lesezugriff termine/schreibzugriff studio/darmstadt/termine.schreibzugriff'} as any),
    newSequenceNumberSubject
  );
  return newSequenceNumberSubject;
};

export {
  isAppClientConfigured,
  listResourceScopes,
  createAppClient,
  createAdminUser,
  createUser,
  needs2FA,
  login,
  logout,
  exchangeAuthCode,
  cleanupLocalStorage,
  incrementAndGet,
};
